<template lang="pug">
.session-details(:class="{ master: hasTracks, 'has-action': hasTracks, condensed, 'has-description': hasDescription }" @click.stop="onCardClick")
    header
        slot(v-if="!hasTracks" name="heading" :item="session")

        template(v-if="hasTracks")
            h4: a.has-action.toggler(v-if="hasTracks" :class="{ open: expandTracks }")
                i.icon-chevron-down
                | {{ title }}

        template(v-else)
            h4 {{ title }}

        .time(v-if="formattedDate") {{ formattedDate }}

    expandable-list(
        v-if="speakers.length"
        item-key="speaker"
        list-class="speakers"
        :items="speakers"
        :max-items="maxItems"
        :component="component"
        :show-more-action="!disabled"
        :params="{ config, showSubtitle, showBottom }")

        template(#more="{ showMore, toggle }")
            a.has-action(@click.stop="toggle" :class="{ open: showMore }")
                | {{ $t(`registration.actions.show_${showMore ? 'less' : 'more'}_speakers`) }}
                i.icon-chevron-down

    expandable-list.sessions(
        v-if="hasTracks"
        item-key="session"
        component="session-details"
        :items="session.track_sessions"
        :max-items="expandTracks ? Infinity : 0"
        :show-more-action="false"
        :params="{ config, condensed }")

        template(#heading="{ item }")
            slot(name="heading" :item="item")
</template>
<script>
// Utils
import { get } from 'lodash';

// Mixins
import maxItems from './max-items.mixin';
import translatable from '@/components/registration/sections/translatable.mixin.js';

// Components
import ExpandableList from '@/components/common/ExpandableList.vue';
import SessionDetailsModal from '@/components/registration/sections/SessionDetailsModal.vue';
import SpeakerCard from '@/components/registration/sections/SpeakerCard.vue';

export default {
    name: 'SessionDetails',

    components: { ExpandableList, SessionDetailsModal, SpeakerCard },

    mixins: [maxItems, translatable],

    props: {
        session: {
            type: Object,
            required: true
        },

        stopPropagation: {
            type: Boolean,
            default: true
        },

        openDetailsOnClick: {
            type: Boolean,
            default: true
        },

        condensed: {
            type: Boolean,
            default: false
        },

        disabled: {
            type: Boolean,
            default: false
        }
    },

    data() {
        const tz = get(this.config, 'event_details.timezone');
        const useEventTimezone = get(this.config, 'event_details.display_timezone') !== 'device';

        return {
            component: SpeakerCard,
            speakers: get(this, 'session.speakers', []),
            hasTracks: Boolean(get(this, 'session.track_sessions', []).length),
            hasDescription: false,
            expandTracks: false,
            lowResItems: 2,
            highResItems: 3,
            showSubtitle: !this.condensed,
            showBottom: !this.condensed,
            translatable_object: this.session,
            tz: useEventTimezone ? tz : Intl.DateTimeFormat().resolvedOptions().timeZone,
            useEventTimezone
        }
    },

    computed: {
        formattedDate() {
            const { locale } = this.$i18n;
            const dates = this.session.dates;
            if (typeof dates !== 'object') {
                return;
            }

            const spansMultipleDays = dates.startDateStr !== dates.endDateStr;
            const prefixWithDay = this.condensed || spansMultipleDays;
            const startTime = dates.startDate.toLocaleString(locale, { hour: 'numeric', minute: 'numeric', timeZone: this.tz });
            const endTime = dates.endDate.toLocaleString(locale, { hour: 'numeric', minute: 'numeric', timeZone: this.tz });
            const startDay = dates.startDate.toLocaleString(locale, { month: 'short', day: 'numeric', timeZone: this.tz });
            const endDay = dates.endDate.toLocaleString(locale, { month: 'short', day: 'numeric', timeZone: this.tz });

            if (spansMultipleDays) {
                return `${startDay}, ${startTime} - ${endDay}, ${endTime}`;
            }

            if (prefixWithDay) {
                return `${startDay}, ${startTime} - ${endTime}`;
            }

            return `${startTime} - ${endTime}`;
        },
    },

    created() {
        this.hasDescription = Boolean(this.getT9Label('description')?.trim().length);
    },

    methods: {
        toggleTracks() {
            if (!this.hasTracks) return;
            this.expandTracks = !this.expandTracks;
        },

        openSessionDetailsModal() {
            if (!this.hasDescription || this.hasTracks) {
                return;
            }

            this.$modal.show(
                SessionDetailsModal,
                {
                    session: this.session,
                    config: this.config
                },
                {
                    adaptive: true,
                    height: 'auto',
                    maxWidth: 584,
                    classes: 'medium'
                }
            );
        },

        onCardClick() {
            if (this.disabled) return;

            this.toggleTracks();

            if (this.openDetailsOnClick) {
                this.openSessionDetailsModal();
            }

            if (!this.stopPropagation) {
                this.$emit('click', this.session);
            }
        }
    }
};
</script>
